import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setToggleInteriorHotspot, setIncrementClickedInterior } from '../../../Redux/Reducers/View360Reducer/View360ActionController';

export const useUspHotspotSeparate = (props, hotspotType = 'EXT') => {
  const dispatch = useDispatch();
  const [currentUspIndex, setCurrentUspIndex] = useState(0);
  
  // Update current USP index with proper state management
  const setCurrentUspIndexSafe = (newIndex, hotspotData) => {
    if (newIndex >= 0 && newIndex < hotspotData.length) {
      setCurrentUspIndex(newIndex);
    }
  };
  
  const [hotspotData, setHotspotData] = useState([]);
  
  const modalList = useSelector((state) => state.view360Reducer.modalList);
  const hotspotDataInterior = useSelector((state) => state.view360Reducer.hotspotDataInterior);
  const hotspotClickedIndex = useSelector((state) => state.view360Reducer.hotspotClickedIndex);
  const dropdownSelection = useSelector((state) => state.view360Reducer.dropdownSelection);
  const interiorHotspotIndex = useSelector(state => state.view360Reducer.interiorHotspotIndex);
  const toggleHotspotView = useSelector(state => state.view360Reducer.toggleHotspotView);
  const is360Index = useSelector(state => state.view360Reducer.is360Index);
  const is360IntIndex = useSelector(state => state.view360Reducer.is360IntIndex);

  const normalizeExteriorData = (exteriorData) => {
    return exteriorData?.map(item => ({
      ...item,
      type: "EXT",
      hotspot_id: item.key,
      feature: item.heading
    })) || [];
  };

  const normalizeInteriorData = (interiorData) => {
    return interiorData?.map(item => ({
      ...item,
      type: "INT",
      heading: item.heading || item.hotspot_id || item.feature || '',
      desc: item.desc || item.feature || '',
      url: item.url || item.hotspot_icon || '',
      key: item.key || item.hotspot_id || item._id || ''
    })) || [];
  };

  useEffect(() => {
    if (hotspotType === 'INT') {
      props?.setInteriorHotspotIndex(-1);
    }
  }, []);

  useEffect(() => {
    let processedData = [];

    if (hotspotType === 'EXT' && modalList?.size) {
      const exteriorArray = Array.from(modalList.entries())?.map(([key, value]) => ({
        key: key,
        ...value
      }));
      processedData = normalizeExteriorData(exteriorArray);
    } else if (hotspotType === 'INT' && hotspotDataInterior.length > 0) {
      processedData = normalizeInteriorData(hotspotDataInterior);
    }

    setHotspotData(processedData);
  }, [modalList, hotspotDataInterior, hotspotType]);

  useEffect(() => {
    if (hotspotType === 'EXT' && hotspotClickedIndex !== -1 && dropdownSelection === "EXT" && hotspotData.length > 0) {
      if (hotspotClickedIndex < hotspotData.length) {
        setCurrentUspIndexSafe(hotspotClickedIndex, hotspotData);
      }
    }
  }, [hotspotClickedIndex, hotspotData, dropdownSelection, hotspotType]);

  useEffect(() => {
    if (hotspotType === 'INT' && interiorHotspotIndex !== -1 && dropdownSelection === "INT" && hotspotData.length > 0) {
      if (interiorHotspotIndex < hotspotData.length) {
        setCurrentUspIndexSafe(interiorHotspotIndex, hotspotData);
      }
    }
  }, [interiorHotspotIndex, hotspotData, dropdownSelection, hotspotType]);

  const handleNext = (e) => {
    e.stopPropagation();
    
    if (hotspotData.length === 0) return;
    
    const nextIndex = (currentUspIndex + 1) % hotspotData.length;
    
    if (hotspotType === 'EXT') {
      // Find the corresponding hotspot key in modalList for View360
      const nextItem = hotspotData[nextIndex];
      if (nextItem && modalList && modalList.size > 0) {
        const modalListKeys = Array.from(modalList.keys());
        const modalListIndex = modalListKeys.findIndex(key => key === nextItem.key);
        
        if (modalListIndex !== -1) {
          setTimeout(() => {
            // Reset Redux state first to ensure change detection works
            props?.setIncrementClicked(-1);
            setTimeout(() => {
              props?.setIncrementClicked(modalListIndex);
            }, 10);
          }, 0);
        }
      }
    } else if (hotspotType === 'INT') {
      // Update View360 state for interior hotspots
      if (hotspotData.length > 1) {
        setTimeout(() => {
          // Reset Redux state first to ensure change detection works
          props?.setIncrementClickedInterior(-1);
          setTimeout(() => {
            props?.setIncrementClickedInterior(nextIndex);
          }, 10);
        }, 0);
      }
    }
    
    // Update USP modal state immediately for responsive UI
    setCurrentUspIndexSafe(nextIndex, hotspotData);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    
    if (hotspotData.length === 0) return;
    
    const prevIndex = currentUspIndex <= 0 ? hotspotData.length - 1 : currentUspIndex - 1;
    
    if (hotspotType === 'EXT') {
      // Find the corresponding hotspot key in modalList for View360
      const prevItem = hotspotData[prevIndex];
      if (prevItem && modalList && modalList.size > 0) {
        const modalListKeys = Array.from(modalList.keys());
        const modalListIndex = modalListKeys.findIndex(key => key === prevItem.key);
        
        if (modalListIndex !== -1) {
          setTimeout(() => {
            // Reset Redux state first to ensure change detection works
            props?.setDecrementClicked(-1);
            setTimeout(() => {
              props?.setDecrementClicked(modalListIndex);
            }, 10);
          }, 0);
        }
      }
    } else if (hotspotType === 'INT') {
      // Update View360 state for interior hotspots
      if (hotspotData.length > 1) {
        setTimeout(() => {
          // Reset Redux state first to ensure change detection works
          props?.setDecrementClickedInterior(-1);
          setTimeout(() => {
            props?.setDecrementClickedInterior(prevIndex);
          }, 10);
        }, 0);
      }
    }
    
    // Update USP modal state immediately for responsive UI
    setCurrentUspIndexSafe(prevIndex, hotspotData);
  };

  // Navigation functions for collapsed modal
  const handleNextCollapsed = (e) => {
    e.stopPropagation();
    const nextIndex = currentUspIndex + 1 >= hotspotData.length ? 0 : currentUspIndex + 1;
    setCurrentUspIndexSafe(nextIndex, hotspotData);
  };

  const handlePrevCollapsed = (e) => {
    e.stopPropagation();
    const prevIndex = currentUspIndex === 0 ? hotspotData.length - 1 : currentUspIndex - 1;
    setCurrentUspIndexSafe(prevIndex, hotspotData);
  };

  // Toggle expand function
  const toggleExpand = (isExpanded, setIsExpanded) => {
    const currentItem = hotspotData[currentUspIndex];
    
    if (isExpanded) {
      // Clear all Redux state when closing
      if (hotspotType === 'EXT') {
        props?.setIncrementClicked(-1);
        props?.setHotspotClickedIndex(-1);
      } else if (hotspotType === 'INT') {
        props?.setInteriorHotspotIndex(-1);
        dispatch(setToggleInteriorHotspot(false));
      }
      setIsExpanded(false);
      props?.setToggleHotspotView(false);
      return;
    }

    if (currentItem) {
      if (hotspotType === 'INT') {
        props?.setToggleHotspotView(true);
        dispatch(setToggleInteriorHotspot(true));
        if (hotspotData.length > 0) {
          dispatch(setIncrementClickedInterior(currentUspIndex));
        }
        
        if (props?.handleSelectThumbnail) {
          props?.handleSelectThumbnail(is360IntIndex, "360Int");
        }
      } else if (hotspotType === 'EXT') {
        if (hotspotData.length > 0) {
          props?.setIncrementClicked(currentUspIndex);
          setCurrentUspIndexSafe(currentUspIndex, hotspotData);
        }
        if (props?.handleSelectThumbnail) {
          props?.handleSelectThumbnail(is360Index, 'threeSixty');
        }
      }
      
      setTimeout(() => {
        setIsExpanded(true);
        props?.setToggleHotspotView(true);
      }, 100);
    }
  };

  // Function to handle initial hotspot clicks and open expanded modal
  const handleHotspotClick = (setIsExpanded) => {
    if (toggleHotspotView && hotspotData.length > 0) {
      const currentItem = hotspotData[currentUspIndex];
      
      if (currentItem) {
        if (hotspotType === 'INT') {
          if (hotspotData.length > 0) {
            // props?.setIncrementClickedInterior(currentUspIndex);
          }
          dispatch(setToggleInteriorHotspot(true));
          props?.setToggleHotspotView(true);
        } else if (hotspotType === 'EXT') {
          if (hotspotData.length > 0) {
            // props?.setIncrementClicked(currentUspIndex);
          }
        }
      }
    }
  };

  // Auto-cycling effect
  const useAutoCycle = (isExpanded, intervalTime = 5000) => {
    useEffect(() => {
      const interval = setInterval(() => {
        if (!isExpanded && hotspotData.length > 0) {
          setCurrentUspIndex((prevIndex) => {
            return prevIndex === hotspotData.length - 1 ? 0 : prevIndex + 1;
          });
        }
      }, intervalTime);
      return () => clearInterval(interval);
    }, [hotspotData.length, isExpanded]);
  };

  const currentUsp = hotspotData[currentUspIndex];
  const currentItemType = currentUsp?.type || hotspotType;
   
  return {
    currentUspIndex,
    setCurrentUspIndex,
    hotspotData,
    currentUsp,
    currentItemType,
    dropdownSelection,
    hotspotClickedIndex,
    interiorHotspotIndex,
    toggleHotspotView,
    handleNext,
    handlePrev,
    handleNextCollapsed,
    handlePrevCollapsed,
    toggleExpand,
    handleHotspotClick,
    useAutoCycle
  };
};
